/**
 * Activity Form JavaScript functionality
 * Includes form validation, Klaviyo integration, submission handling, etc.
 */

class ActivityForm {
  constructor(formId) {
    this.form = document.getElementById(formId);
    this.formId = formId;
    this.init();
  }

  init() {
    if (!this.form) {
      console.error('Activity form not found');
      return;
    }

    // Wait for Flatpickr to be available
    if (typeof flatpickr === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    this.setupDateTimeValidation();
    this.bindEvents();
  }

  bindEvents() {
    // Form submission event
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Real-time validation events
    const inputs = this.form.querySelectorAll('input, select');
    inputs.forEach(input => {
      input.addEventListener('blur', (e) => this.handleBlur(e));
      input.addEventListener('input', (e) => this.handleInput(e));
    });
  }

  /**
   * Handle form submission
   */
  handleSubmit(e) {
    // 让Shopify处理表单提交，但先准备邮件内容
    if (!this.validateForm()) {
      e.preventDefault();
      this.showError('Please fill in all required fields');
      return;
    }

    // 准备邮件内容
    this.prepareEmailContent();
    
    // 发送到Klaviyo（如果可用）
    this.sendToKlaviyoAsync();
  }

  /**
   * Collect form data
   */
  collectFormData() {
    const formData = new FormData(this.form);
    return {
      name: formData.get('contact[name]'),
      email: formData.get('contact[email]'),
      phone: formData.get('contact[phone]'),
      appointment_time: formData.get('contact[appointment_time]'),
      store: formData.get('contact[store]'),
      form_type: 'activity_registration'
    };
  }

  /**
   * Prepare email content for Shopify contact form
   */
  prepareEmailContent() {
    const formData = this.collectFormData();
    // Format the appointment time properly
    const appointmentDate = formData.appointment_time ? formData.appointment_time : 'Not specified';

    const emailContent = `
Thank you for participating in Aimer's Pink Ribbon Month charity initiative. Your appointment details are as follows:

Name: ${formData.name}
Date & Time: ${appointmentDate}
Location: ${formData.store}

Please arrive on time, our staff will provide professional fitting services.

Contact Information:
Email: ${formData.email}
Phone: ${formData.phone}

Submitted: ${new Date().toLocaleString('en-US')}
    `.trim();

    // 设置隐藏的body字段
    const bodyInput = this.form.querySelector('input[name="contact[body]"]');
    if (bodyInput) {
      bodyInput.value = emailContent;
    }
  }

  /**
   * Setup Flatpickr date time picker with configurable constraints
   */
  setupDateTimeValidation() {
    const appointmentInput = this.form.querySelector('input[name="contact[appointment_time]"]');
    if (!appointmentInput) return;

    // Get configuration from data attributes
    const minDate = appointmentInput.dataset.minDate;
    const maxDate = appointmentInput.dataset.maxDate;
    const minTime = appointmentInput.dataset.minTime;
    const maxTime = appointmentInput.dataset.maxTime;

    // Calculate effective min date using local timezone
    const today = new Date();
    const todayStr = today.getFullYear() + '-' + 
                    String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                    String(today.getDate()).padStart(2, '0');
    
    let effectiveMinDate = minDate;
    
    // Determine if we should use today as min date
    if (minDate && maxDate) {
      if (todayStr >= minDate && todayStr <= maxDate) {
        // Today is within range, so use today as min date
        effectiveMinDate = todayStr;
      }
    } else if (minDate && todayStr >= minDate) {
      // Only minDate is set and today is after minDate, use today
      effectiveMinDate = todayStr;
    } else if (!minDate) {
      // No minDate configured, use today
      effectiveMinDate = todayStr;
    }

    // Configure Flatpickr options
    const flatpickrOptions = {
      enableTime: true,
      dateFormat: "Y-m-d H:i",
      time_24hr: true,
      minDate: effectiveMinDate || undefined,
      maxDate: maxDate || undefined,
      minTime: minTime || undefined,
      maxTime: maxTime || undefined,
      onReady: function(selectedDates, dateStr, instance) {
        // Add custom styling
        instance.calendarContainer.classList.add('activity-flatpickr');
      }
    };

    // Initialize Flatpickr
    this.flatpickrInstance = flatpickr(appointmentInput, flatpickrOptions);
  }







  /**
   * Form validation
   */
  validateForm() {
    const fields = {
      name: this.form.querySelector('input[name="contact[name]"]'),
      email: this.form.querySelector('input[name="contact[email]"]'),
      phone: this.form.querySelector('input[name="contact[phone]"]'),
      appointmentTime: this.form.querySelector('input[name="contact[appointment_time]"]'),
      store: this.form.querySelector('select[name="contact[store]"]')
    };

    let isValid = true;

    // Clear previous error styles
    Object.values(fields).forEach(field => {
      if (field) field.classList.remove('has-errors');
    });

    // Validate name
    if (!fields.name.value.trim()) {
      fields.name.classList.add('has-errors');
      isValid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!fields.email.value.trim() || !emailRegex.test(fields.email.value)) {
      fields.email.classList.add('has-errors');
      isValid = false;
    }

    // Validate phone
    const phoneRegex = /^[\d\-\s\(\)]+$/;
    if (!fields.phone.value.trim() || !phoneRegex.test(fields.phone.value)) {
      fields.phone.classList.add('has-errors');
      isValid = false;
    }

    // Validate appointment time
    if (!fields.appointmentTime.value) {
      fields.appointmentTime.classList.add('has-errors');
      isValid = false;
    }

    // Validate store selection
    if (!fields.store.value) {
      fields.store.classList.add('has-errors');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Send to Klaviyo asynchronously (non-blocking)
   */
  async sendToKlaviyoAsync() {
    try {
      const formData = this.collectFormData();
      await this.sendToKlaviyo(formData);
    } catch (error) {
      console.error('Klaviyo send failed:', error);
      // 不阻止表单提交，即使Klaviyo失败
    }
  }


  /**
   * Send data to Klaviyo
   */
  async sendToKlaviyo(activityData) {
    return new Promise((resolve, reject) => {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        try {
          // Use the appointment time as formatted by Flatpickr
          const formattedAppointmentTime = activityData.appointment_time || 'Not specified';

          // Identify user
          window.klaviyo.identify({
            '$email': activityData.email,
            'Name': activityData.name,
            '$phone_number': activityData.phone,
            'Store': activityData.store,
            'Appointment Time': formattedAppointmentTime
          });

          // Track activity registration event
          window.klaviyo.track('Activity Registration', {
            'Appointment Time': formattedAppointmentTime,
            'Store': activityData.store,
            'Form Type': activityData.form_type,
            'Registration Date': new Date().toISOString()
          });

          console.log('Klaviyo data sent successfully');
          resolve({ success: true, message: 'Klaviyo data sent successfully' });
        } catch (error) {
          console.error('Klaviyo send failed:', error);
          resolve({ success: false, message: 'Klaviyo send failed', error: error });
        }
      } else {
        console.log('Klaviyo not loaded properly, please check settings');
        resolve({ success: false, message: 'Klaviyo not available' });
      }
    });
  }

  /**
   * Set loading state
   */
  setLoadingState(button, isLoading, originalText = 'Submit') {
    if (isLoading) {
      button.textContent = 'Submitting...';
      button.disabled = true;
      button.classList.add('loading');
    } else {
      button.textContent = originalText;
      button.disabled = false;
      button.classList.remove('loading');
    }
  }

  /**
   * Show error message
   */
  showError(message) {
    let errorMessage = document.querySelector('.form-status__message--error');
    if (!errorMessage) {
      errorMessage = document.createElement('div');
      errorMessage.className = 'form-status__message form-status__message--error';
      this.form.parentNode.insertBefore(errorMessage, this.form);
    }
    errorMessage.innerHTML = message;
  }

  /**
   * Hide form
   */
  hideForm() {
    this.form.style.display = 'none';
  }


  /**
   * Handle input blur event
   */
  handleBlur(e) {
    if (e.target.value.trim()) {
      e.target.classList.remove('has-errors');
    }
  }

  /**
   * Handle input event
   */
  handleInput(e) {
    if (e.target.classList.contains('has-errors') && e.target.value.trim()) {
      e.target.classList.remove('has-errors');
    }
  }
}

/**
 * Initialize activity form
 */
function initActivityForm() {
  // Wait for DOM to load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      const formId = 'ActivityForm-' + document.querySelector('[data-section-type="activity"]')?.dataset.sectionId;
      if (formId) {
        new ActivityForm(formId);
      }
    });
  } else {
    const formId = 'ActivityForm-' + document.querySelector('[data-section-type="activity"]')?.dataset.sectionId;
    if (formId) {
      new ActivityForm(formId);
    }
  }
}

// Auto initialize
initActivityForm();

// Export class for other scripts to use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ActivityForm;
}
